import { getLocaleUtilsForLanguage } from '../rules/rules-store';
import { LocaleRuleTypes } from '../types';

type LocaleRuleType = (typeof LocaleRuleTypes)[keyof typeof LocaleRuleTypes];
/**
 * Generates a locale reference string based on the provided type, id, scope, and path.
 * The generated reference is in uppercase and follows a specific format based on the type.
 * @param type The type of the reference (e.g., DATAPAGE, VIEW, FIELD, etc.).
 * @param id The unique identifier for the reference (e.g., View name, Paragraph name, etc.).
 * @param ruleClass Optional Rule Class for scoped types (View, Field, Paragraph).
 * @param path Optional path for types that support it. Currently only DATAPAGE and PARAGRAPH support path.
 * @returns The generated locale reference string.
 *
 * <code>
 * // For Datapage type
 *   const dataPageRef = getLocaleReference(LocaleRuleTypes.DATAPAGE, 'D_SomeDataPage', null, 'pyLabel');
 * // For View type
 *   const viewRef = getLocaleReference(LocaleRuleTypes.VIEW, 'pyDetails', '@baseclass');
 * // For Field type
 *  const fieldRef = getLocaleReference(LocaleRuleTypes.FIELD, 'FirstName', 'Data-Person');
 * // For Case type
 *  const caseRef = getLocaleReference(LocaleRuleTypes.CASE, 'ServiceRequest', 'Work-ServiceRequest');
 * // For Paragraph type with decisioning, if no decisioning, path can be omitted
 *  const paraRef = getLocaleReference(LocaleRuleTypes.PARAGRAPH, 'MyParagraph', 'Work-MyCaseType', 'isAgeAbove18');
 * // For Portal type
 *  const portalRef = getLocaleReference(LocaleRuleTypes.PORTAL, 'WebPortal', 'Work-');
 * </code>
 */
export const getLocaleReference = (type: LocaleRuleType, id: string, ruleClass?: string, path?: string): string => {
  const scopedTypes = [LocaleRuleTypes.VIEW, LocaleRuleTypes.FIELD, LocaleRuleTypes.PARAGRAPH] as LocaleRuleType[];
  const typesWithPath = [LocaleRuleTypes.DATAPAGE, LocaleRuleTypes.PARAGRAPH] as LocaleRuleType[];
  if (!id || (scopedTypes.includes(type) && !ruleClass)) {
    console.warn(
      `Invalid parameters for locale reference: type=${type}, id=${id}, ruleClass=${ruleClass}, path=${path}`
    );
  }
  const pathPrefix = typesWithPath.includes(type) && path ? `#${path}` : '';
  const scopePrefix = type === LocaleRuleTypes.DATAPAGE ? '@BASECLASS' : ruleClass;
  if (type === LocaleRuleTypes.DATAPAGE || scopedTypes.includes(type)) {
    return `${scopePrefix}!${type}!${id}${pathPrefix}`.toUpperCase();
  }
  return `${type}!${id}${pathPrefix}`.toUpperCase();
};

/**
 * The LocalizationService class is responsible for providing localized strings based on the locale.
 * It retrieves localization fields from a language pack store and allows for token replacement in localized strings.
 * The class constructor takes an locale string and optional locale reference and path.
 * The localization fields are retrieved from the language pack store based on the provided locale and locale reference.
 */
export class LocalizationService {
  private readonly locale: string;
  private readonly localeReference?: string;
  static readonly COMMON_LABELS = 'COMMONLABELS';
  private static readonly MESSAGES = 'MESSAGES';
  private static readonly COMPONENT_FIELDS = 'COMPONENTFIELDS';
  private lookupList: string[] = [];

  /**
   * @param locale locale string.
   * @param localeReference Optional reference to a specific rule.
   * The constructor initializes the locale, locale reference, and localization fields.
   */
  constructor(locale: string, localeReference?: string) {
    this.localeReference = localeReference;
    this.locale = locale;
  }

  /**
   * Retrieves the localized value for a given key and replaces tokens with provided arguments.
   * @function
   * @param input text for localization.
   * @param args Optional arguments to replace tokens in the localized string.
   * @returns The localized string with tokens replaced, or the Input value if not found.
   */
  getLocalizedText(input: string, args?: string[]): string {
    const languagePackStore = getLocaleUtilsForLanguage(this.locale);
    const localizedString =
      this.#getLocalizedFields(
        input,
        this.localeReference ? languagePackStore.localeStore[`${this.localeReference.toUpperCase()}`] : {}
      ) ||
      this.#getLocalizedFields(input, languagePackStore.localeStore[LocalizationService.COMMON_LABELS]) ||
      this.#getLocalizedFields(input, languagePackStore.localeStore[LocalizationService.COMPONENT_FIELDS]) ||
      this.#getLocalizedFieldsFromLookupList(input) ||
      input;

    const translatedText = args ? this.#resolveTokens(localizedString, args) : localizedString;
    return translatedText || input;
  }

  /**
   * Retrieves the localized message for a given key from the Messages locale fields.
   * @param input text for localization.
   * @param args Optional arguments to replace tokens in the localized string.
   * @returns The localized message string with tokens replaced, or the key if not found.
   */
  getLocalizedMessage(input: string, args?: string[]): string {
    const languagePackStore = getLocaleUtilsForLanguage(this.locale);
    const messageStore = languagePackStore.localeStore[LocalizationService.MESSAGES] ?? {};
    const message = this.#getLocalizedFields(input, messageStore) ?? input;
    const translatedText = args ? this.#resolveTokens(message, args) : message;
    return translatedText || input;
  }

  /**
   * Retrieves the localized data page text for a given data page name and field name.
   * @param input text for localization.
   * @param dataPageName The name of the data page.
   * @param fieldName The name of the field within the data page.
   * @param args Optional arguments to replace tokens in the localized string.
   * @returns The localized text for the data page field, or the input value if not found.
   * <example>
   * getLocalizedDataPage('Hello {1}', 'D_Address', 'greeting', ['World']);
   * </example>
   */
  getLocalizedDataPage(input: string, dataPageName: string, fieldName: string, args?: string[]): string {
    if (!dataPageName || !fieldName || !input) {
      return input;
    }
    const localeReference = getLocaleReference(LocaleRuleTypes.DATAPAGE, dataPageName, undefined, fieldName);
    const languagePackStore = getLocaleUtilsForLanguage(this.locale);
    const localizedText =
      this.#getLocalizedFields(input, languagePackStore.localeStore[localeReference]) ??
      this.#getLocalizedFields(input, languagePackStore.localeStore[LocalizationService.COMMON_LABELS]) ??
      this.#getLocalizedFieldsFromLookupList(input) ??
      input;
    const translatedText = args ? this.#resolveTokens(localizedText, args) : localizedText;
    return translatedText || input;
  }

  /**
   * Resolves tokens in the input string with the provided arguments.
   * @param input text for localization.
   * @param args arguments to replace tokens in the localized string.
   * @returns The input string with tokens replaced by the corresponding arguments.
   * @private
   */
  #resolveTokens(input: string, args: string[]): string {
    if (args.length === 0) {
      return input;
    }
    return input.replace(/\{(\d+)\}/g, (_, token) => {
      return args[`${Number(token) - 1}`] ?? _;
    });
  }

  /**
   *
   * @param input text for localization.
   * This method retrieves localized fields from the lookup list if it exists.
   * @returns The localized string or undefined if not found.
   * @private
   */
  #getLocalizedFieldsFromLookupList(input: string): string | undefined {
    if (this.lookupList.length === 0) {
      return;
    }
    const languagePackStore = getLocaleUtilsForLanguage(this.locale);
    for (const key of this.lookupList) {
      const localized = this.#getLocalizedFields(input, languagePackStore.localeStore[key]);
      if (localized) {
        return localized;
      }
    }
  }

  /**
   * Retrieves the localized fields for a given key from the localization fields.
   * @param input text for localization.
   * @param localeFields The locale fields to search for the input key.
   * @returns The localized string or undefined if not found.
   * @private
   */
  #getLocalizedFields(input: string, localeFields: { [key: string]: string }): string | undefined {
    if (input === undefined || input === null || !localeFields) {
      return;
    }
    const targetText = localeFields[input] ?? localeFields[input];
    if (typeof targetText !== 'string') {
      return;
    }
    return targetText;
  }

  /**
   *
   * @param lookupList An array of strings representing the lookup list.
   * This method sets the lookup list for the LocalizationService instance.
   * It expects an array of strings and assigns it to the lookupList property.
   * @protected
   * @example
   * service.setLookupList(['CASE!SERVICEREQUEST', 'PORTAL!WEBPORTAL']);
   */
  setLookupList(lookupList: string[]): void {
    if (Array.isArray(lookupList)) {
      this.lookupList = lookupList;
    }
  }
}
